<template>
  <EditionModal
    :is-displayed="inPopup || id !== null"
    :query-param="pbSheetQueryParam"
    class="problem-sheet"
    :class="sheetClasses"
  >
    <FiltersZone v-if="!inPopup" :modal-title="$t('message.OPTIONS')">
      <UsersSync slot="shortcuts" :entity-id="id || 0" entity-type="pb-sheet" />
      <div>
        <button class="generic-border generic-border--text-btn generic-border--blue" @click="exportToXl">
          <Icon>{{ icons.downloadMedium }}</Icon>
          <span>{{ $t('message.EXPORT_TO_EXCEL') }}</span>
        </button>
        <div class="mt10em">
          <button
            v-if="problem && problem.archived === 0"
            class="generic-border generic-border--text-btn generic-border--blue"
            @click="archiveProblem"
          >
            <Icon>{{ icons.archivesMedium }}</Icon>
            <span>{{ $t('message.ARCHIVE') }}</span>
          </button>
          <button
            v-else
            class="generic-border generic-border--text-btn generic-border--blue"
            @click="restoreProblem"
          >
            <Icon>{{ icons.cancelMedium }}</Icon>
            <span>{{ $t('message.RESTORE') }}</span>
          </button>
        </div>
        <button
          class="generic-border generic-border--text-btn generic-border--blue mt10em"
          @click="isHistoryDisplayed = true"
        >
          <Icon>{{ icons.clockMedium }}</Icon>
          <span>{{ $t('message.HISTORY') }}</span>
        </button>
        <div class="problem-sheet__action-states">
          <h3>{{ $t('message.ACTIONS') }}</h3>
          <div
            v-for="actionState in actionStates"
            :key="actionState.title"
            class="problem-sheet__action-states__state"
          >
            <div class="v-centerer generic-background">
              <div class="legend">{{ actionState.title }}</div>
              <div class="generic-border problem-sheet__action-states__state__count">{{ actionState.count }}</div>
            </div>
          </div>
        </div>
        <!-- Filter actions -->
        <h2 class="basic-sub-title">
          {{ $t('message.FILTERS') }}
        </h2>
        <EditableLabelRadio
          v-model="actionsPdcaFilter"
          :items="actionsPdcaFilterOptions"
          :legend="`${$t('message.PDCA')}${$t('message.TWO_DOTS')}`"
          multiple
        />
        <!-- Comments -->
        <h2 class="basic-sub-title">
          {{ $t('message.COMMENTS') }}
        </h2>
        <Comment
          v-for="comment in comments"
          :key="comment.id"
          :comment="comment"
          @edit="editComment(comment)"
          @delete="deleteComment(comment)"
        />
        <button
          class="generic-border generic-border--text-btn generic-border--blue-border f-w"
          @click="editComment(null)"
        >
          <div class="generic-border__left">
            <Icon class="generic-border__left-blue-icon">{{ icons.addExtraBold }}</Icon>
            <span>{{ $t('message.ADD_A_COMMENT') }}</span>
          </div>
          <Icon class="generic-border__right-arrow">{{ icons.arrowDownLine }}</Icon>
        </button>
      </div>
    </FiltersZone>


    <div v-if="problem" class="f-h-below-filters">
      <div class="problem-sheet__details">

        <!-- Summary top left -->
        <ProblemSummarySection
          class="problem-sheet__details__summary generic-border--card"
          :problem="problem"
          :disabled="noEditionAllowed"
        />

        <!-- Problem details top right (impact, deadline, ...) -->
        <ProblemDetailsSection
          class="problem-sheet__details__pb-data generic-border--card"
          :problem="problem"
          :disabled="noEditionAllowed"
          :display-recurrence-field="!inPopup"
        />
      </div>

      <div class="problem-sheet__data">

        <!-- 5W1H -->
        <ProblemDataSection
          ref="dataSection"
          class="problem-sheet__data__w5h1 generic-border--card"
          :problem="problem"
          :disabled="noEditionAllowed"
        />

        <!-- Causes -->
        <ProblemCausesSection
          ref="causesSection"
          class="problem-sheet__data__causes generic-border--card"
          :problem="problem"
          :disabled="noEditionAllowed"
          :selected-cause-id="selectedCauseId"
          @selectCause="selectCause"
        />

        <!-- Actions -->
        <ProblemActionsSection
          ref="actionsSection"
          class="problem-sheet__data__actions generic-border--card"
          :problem="problem"
          :disabled="noEditionAllowed"
          :selected-cause-id="selectedCauseId"
          :actions-pdca-filter="actionsPdcaFilter"
          :select-all-actions="selectAllActions"
          @selectAllActions="selectAllActions = $event"
        />
      </div>
    </div>

    <Modal v-model="isHistoryDisplayed" @input="isHistoryDisplayed = false">
      <div class="problem-sheet__history">
        <h3>
          {{ $t('message.HISTORY') }}
        </h3>

        <div v-if="history.length === 0" class="field-hint">
          {{ $t('message.HISTORY_EMPTY') }}
        </div>
        <HistoryItem
          v-for="item in history"
          :key="item.id"
          :item="item"
          :all-indicators="allIndicators"
          :all-causes="allCauses"
          :all-actions="problem ? problem.actions : []"
          :all-action-plans="mergedActionPlans"
        />
      </div>
    </Modal>
  </EditionModal>
</template>

<script>
/**
 * An overlay page that displays the details of a problem, its causes and their actions
 *
 * @vue-prop {Number} [propId] - When the problem sheet is used as a sub-component,
 *                             you must specify the id of the problem here
 * @vue-prop {Boolean} [inPopup] - Set to tru to use the problem sheet as a sub-component
 *
 * TODO Deadline postponment
 */

import $ from 'jquery';
import toastr from 'toastr';
import { cloneDeep, isEqual } from 'lodash';
// Vuex
import { mapState } from 'vuex';
import { GET_ZONES, GET_PB_TYPES } from '@/store/mutation-types';
import {
  FETCH_SINGLE_PROBLEM,
  FETCH_PROBLEM_HISTORY,
  ARCHIVE_PROBLEM,
  RESTORE_PROBLEM,
} from '@/store/modules/problem/action-types';
// Inport components
import Comment from '@/components/comment.vue';
import EditableLabelRadio from '@/components/editable-label-radio.vue';
import EditionModal from '@/components/edition-modals/edition-modal.vue';
import FiltersZone from '@/components/filters-zone.vue';
import HistoryItem from '@/components/history-item.vue';
import Icon from '@/components/icon.vue';
import Modal from '@/components/modal.vue';
import ProblemActionsSection from '@/components/problem-sheet/problem-actions-section.vue';
import ProblemCausesSection from '@/components/problem-sheet/problem-causes-section.vue';
import ProblemDataSection from '@/components/problem-sheet/problem-data-section.vue';
import ProblemDetailsSection from '@/components/problem-sheet/problem-details-section.vue';
import ProblemSummarySection from '@/components/problem-sheet/problem-summary-section.vue';
import UsersSync from '@/components/users-sync.vue';
// Import socket handling utils
import mixinSocketHandler from '@/mixins/socket-handler.vue';
import SocketMessages from '@/utils/classes/SocketMessages';
// Import utils
import IAAnalyser from '@/utils/classes/IAAnalyser';
import IAHelpStep from '@/utils/classes/IAHelpStep';
import IATeacher from '@/utils/classes/IATeacher';
import IAHelp from '@/utils/classes/IAHelp';
import { calert, nestedFlatten } from '@/utils/main';
import { exportToExcel } from '@/utils/problem-utils';
import { isActionCompleted } from '@/utils/action-utils';
import * as icons from '@/assets/icons';
import { pbSheetQueryParam } from '@/utils/navigation-constants';
import { HistoryChangeType } from '@/utils/classes/HistoryChangeType';

export default {

  name: 'ProblemSheetModal',

  components: {
    Comment,
    EditableLabelRadio,
    EditionModal,
    FiltersZone,
    HistoryItem,
    Icon,
    Modal,
    ProblemActionsSection,
    ProblemCausesSection,
    ProblemDataSection,
    ProblemDetailsSection,
    ProblemSummarySection,
    UsersSync,
  },

  mixins: [mixinSocketHandler],

  props: {
    inPopup: Boolean,
    showOnlyProblemData: Boolean,
    propId: {
      type: Number,
      default: null,
    },
  },

  data() {
    return {
      icons,
      pbSheetQueryParam,
      problem: null,
      // Options in FiltersZone
      comments: [],
      isHistoryDisplayed: false,
      mergedActionPlans: [],
      actionsPdcaFilterOptions: [
        { id: 0, label: this.$t('message.ACTION_STATE_PENDING') },
        { id: 1, label: this.$t('message.PDCA_PLAN') },
        { id: 2, label: this.$t('message.PDCA_DO') },
        { id: 3, label: this.$t('message.PDCA_CHECK') },
        { id: 4, label: this.$t('message.PDCA_ACT') },
      ],
      // Properties used by other components. See src/views/readme.md
      pageBackButtonAction: () => {
        this.closeModal();
      },
      iaAnalysisText: '',
      iaTeachText: this.$t('sensei.TEACHER_TEXT'),
      iaTeachBtns: [
        { text: this.$t('sensei.W5H1_TITLE'), action: () => { IATeacher.lesson('W5H1'); } },
        { text: this.$t('sensei.FIVE_WHYS_TITLE'), action: () => { IATeacher.lesson('FIVE_WHYS'); } },
        { text: this.$t('sensei.ISHIKAWA_TITLE'), action: () => { IATeacher.lesson('ISHIKAWA'); } },
        { text: this.$t('sensei.PLAN_ACTION_TITLE'), action: () => { IATeacher.lesson('PLAN_ACTION'); } },
      ],
    };
  },

  computed: {
    /** Number: ID of the problem. If 0, creating a new problem */
    id() {
      if (this.inPopup) {
        return this.propId;
      }
      return this.$route.query[pbSheetQueryParam] === undefined
        ? null
        : Number(this.$route.query[pbSheetQueryParam]);
    },
    sheetClasses() {
      return {
        'problem-sheet--in-popup': this.inPopup,
        'problem-sheet--only-problem': this.showOnlyProblemData,
      };
    },
    // Did the current team has been granted access to this problem
    accessGranted() {
      return this.currentTeam.id === Number(this.problem.affectTeam)
        || (this.problem.accesses || []).some(teamId => this.currentTeam.id === Number(teamId))
        || (this.currentUser && this.currentUser.teams.includes(Number(this.problem.affectTeam)));
    },
    // Forbid edition if problem isn't loaded or the current team has no access right
    noEditionAllowed() {
      return !this.problem || !this.accessGranted;
    },
    /** Array: Users of the teams assigned to this problem */
    allUsers() {
      return this.$store.getters['problem/radioUsers'](this.problem.id);
    },
    /** Problem as it was last set in the state */
    stateProblem() {
      return this.$store.state.problem.problems[`${this.id}`];
    },
    /** Array: History raw data from state */
    stateHistory() {
      return this.$store.state.problem.history[`${this.problem.id}`] || [];
    },
    /**
      * History built data
      *
      * Prevent having history changes for the end of creation date and
      * when the 'fromValue' equals the 'toValue'.
      * But when the "files" field is edited, fromValue and toValue are both empty,
      * this it would be filtered out if we weren't specifying it.
      */
    history() {
      // Fetch all the history changes related to this problem (including causes and actions changes)
      const history = cloneDeep(this.$store.state.problem.history[`${this.id}`]) || [];
      const excludedFields = ['endOfCreation', 'solution.text', 'solution.files'];
      const { edition } = HistoryChangeType;
      return history
        .filter(item => (item.field)
          && (!excludedFields.includes(item.field))
          && (item.field === 'files' || item.type !== edition || item.fromValue !== item.toValue))
        .map(item => ({ ...item, trSuffix: 'PB' }));
    },
    historyLoading() {
      return !!this.$store.state.problem.historyLoading[`${this.id}`];
    },
    /** Array: Initial value of actionsPdcaFilter */
    actionsPdcaFilterInitial() {
      return this.actionsPdcaFilterOptions.map(opt => opt.id);
    },
    /** Array: Numbers of pending, ongoing and completed actions */
    actionStates() {
      const actions = (this.problem && this.problem.actions) || [];
      return [
        {
          title: this.$t('message.ACTION_STATE_PENDING'),
          count: actions.filter(action => action.state === 0).length,
        },
        {
          title: this.$t('message.ACTION_STATE_ONGOING'),
          count: actions.filter(action => action.state === 1 && !isActionCompleted(action)).length,
        },
        {
          title: this.$t('message.ACTION_STATE_COMPLETED'),
          count: actions.filter(action => isActionCompleted(action)).length,
        },
      ];
    },
    /** Boolean: Whether all actions should be displayed */
    selectAllActions: {
      get() {
        return !this.selectedCauseId
          && isEqual(cloneDeep(this.actionsPdcaFilter).sort(), cloneDeep(this.actionsPdcaFilterInitial).sort());
      },
      set(doSelect) {
        if (doSelect) {
          this.actionsPdcaFilter = this.actionsPdcaFilterInitial;
          this.selectedCauseId = 0;
        }
      },
    },
    /** Array: IDs of the selected option in PDCA filter */
    actionsPdcaFilter: {
      get() {
        if (this.$route.query.sheetPdcaFilter !== undefined) {
          const { sheetPdcaFilter } = this.$route.query;
          return sheetPdcaFilter === '' ? [] : sheetPdcaFilter.split(',').map(id => Number(id));
        }
        return cloneDeep(this.actionsPdcaFilterInitial);
      },
      set(optionIds) {
        const query = Object.assign({}, { ...this.$route.query, sheetPdcaFilter: optionIds.join(',') });
        if (!isEqual(query, this.$route.query)) {
          this.$router.replace({ query });
        }
      },
    },
    /** Number:  ID of the cause currently selected */
    selectedCauseId: {
      get() {
        return Number(this.$route.query.pbSheetSelectedCause || 0);
      },
      set(causeId) {
        const query = Object.assign({}, { ...this.$route.query, pbSheetSelectedCause: causeId || undefined });
        if (!isEqual(query, this.$route.query)) {
          this.$router.replace({ query });
        }
      },
    },
    /** All the causes for this problem in a linear array */
    allCauses() {
      if (!this.problem) {
        return [];
      }
      return nestedFlatten(this.problem.wCauses || [], 'wCauses');
    },
    ...mapState({
      allZones: state => state[GET_ZONES.stateKey] || [],
      allTypes: state => state[GET_PB_TYPES.stateKey] || [],
      allIndicators(state) {
        if (!this.problem) {
          return [];
        }
        return (state.indicatorsArrayPerTeam[`${this.problem.affectTeam}`] || {}).data || [];
      },
    }),
    // Properties used by other components. See src/views/readme.md
    pageTitle() {
      return this.stateProblem ? this.stateProblem.lWhat : this.$t('message.PROBLEM');
    },
  },

  watch: {
    'problem.actions': {
      handler() {
        if (!this.historyLoading && this.problem) {
          this.$store.dispatch(`problem/${FETCH_PROBLEM_HISTORY}`, { problemId: this.problem.id });
        }
      },
      deep: true,
    },
    stateProblem: {
      deep: true,
      handler(newVal) {
        if (newVal) {
          this.problem = cloneDeep(newVal);
        }
      },
    },
    isActionCreationDisplayed() {
      if (!this.isActionCreationDisplayed && this.id > 0) {
        // Refresh the problem whenever an action modal is closed (isActionCreationDisplayed in src/main)
        this.refreshProblem();
      }
    },
    id() {
      if (this.id !== null) {
        this.initializePage();
      } else {
        this.problem = null;
      }
    },
  },

  created() {
    if (this.id !== null) {
      this.initializePage();
    }
    this.registerSocketHandlers();
  },

  methods: {
    /** Register all socket handlers */
    registerSocketHandlers() {
      this.handleSocketMessage(SocketMessages.CC_MSGS.PROBLEM_SHEET, (msg) => {
        if (Number(msg.id) !== Number(this.problem.id)) return;
        this.refreshProblem();
      });
      this.handleSocketMessage(SocketMessages.CC_MSGS.PROBLEM_COMMENTS, (msg) => {
        if (Number(msg.id) !== Number(this.problem.id)) return;
        this.refreshComments();
      });
      this.handleSocketMessage(SocketMessages.CC_MSGS.PROBLEM_ACCESS, (msg) => {
        if (Number(msg.id) !== Number(this.problem.id)) return;
        this.fetchAccesses();
      });
    },
    /** Used to be the beforeRouteEnter content. Now both for beforeRouteEnter and beforeRouteUpdate */
    initializePage() {
      // fetch the problem and the company data (like zones, people names, indicators of the current team, ...)
      this.$store.dispatch(`problem/${FETCH_SINGLE_PROBLEM}`, { id: this.id, includeData: true })
        .then((d) => {
          this.mergedActionPlans = Object.values(d.plans).flatMap(plans => plans);
          // Actions not linked to a cause
          this.selectedCause = null;
        })
        .catch((d) => {
          const button = this.inPopup
            ? { title: this.$t('message.OK') }
            : { title: this.$t('message.PB_GO_BACK'), action: () => { this.$router.back(); } };
          if (d === -2) {
            // No problem found
            calert(this.$t('message.PB_NOT_FOUND'), [button]);
          } else if (d === -1) {
            // Access not granted
            calert(this.$t('message.PB_NO_ACCESS'), [button]);
          }
        });
      this.refreshComments();
    },
    /** Export problem sheet to xlsx */
    exportToXl() {
      exportToExcel(this.problem, this.allIndicators, this.allZones, this.allTypes, this.allUsers);
    },
    /** Fetch access list of the current problem displayed */
    fetchAccesses() {
      if (!this.problem) {
        return;
      }
      this.$http.post('/server/problems/getAccesses.php', { id: this.problem.id }).then(({ data }) => {
        if (data instanceof Array) {
          this.problem.accesses = data;
        } else {
          toastr.error(this.$t('message.CONNECTION_ERROR'));
        }
      });
    },
    /** Select / unselect a cause */
    selectCause(cause) {
      if (cause === null) {
        this.selectedCauseId = 0;
      } else {
        this.selectedCauseId = this.selectedCauseId === cause.id ? 0 : cause.id;
      }
    },
    // TODO: Component for comments list (which may be reused for actions for instance)
    /** Fetch comments */
    refreshComments() {
      this.$http.post('/server/comments/get.php', { itemId: this.id, itemType: 0 }).then(({ data }) => {
        this.comments = Array.isArray(data) ? data : this.comments;
      });
    },
    editComment(comment) {
      comment = comment || { id: 0, text: '' };
      const params = {
        id: comment.id,
        itemType: 0,
        itemId: this.id,
        text: comment.text,
      };
      this.$http.post('/server/comments/set.php', params).then(({ data }) => {
        if (Array.isArray(data)) {
          this.comments = data;
          toastr.success(this.$t('message.COMMENT_SAVED'));
          this.sendSocketMessage({
            msg: SocketMessages.CC_MSGS.PROBLEM_COMMENTS,
            id: this.problem.id,
            socketCompany: 1,
          });
        } else {
          toastr.error(this.$t('message.CONNECTION_ERROR'));
        }
      });
    },
    deleteComment(comment) {
      const params = { id: comment.id, itemType: 0, itemId: this.id };
      this.$http.post('/server/comments/delete.php', params).then(({ data }) => {
        if (Array.isArray(data)) {
          this.comments = data;
          this.sendSocketMessage({
            msg: SocketMessages.CC_MSGS.PROBLEM_COMMENTS,
            id: this.problem.id,
            socketCompany: 1,
          });
        } else {
          toastr.error(this.$t('message.CONNECTION_ERROR'));
        }
      });
    },
    /** Send a socket msg to notify teams that this problem sheet can be refreshed */
    sendSocketUpdate() {
      this.sendSocketMessage({ msg: SocketMessages.CC_MSGS.PROBLEM_SHEET, id: this.problem.id, socketCompany: 1 });
    },
    /** Refresh problem from refresh prompt */
    refreshProblem() {
      this.$store.dispatch(`problem/${FETCH_SINGLE_PROBLEM}`, { id: this.problem.id });
    },
    archiveProblem() {
      this.$store.dispatch(`problem/${ARCHIVE_PROBLEM}`, { id: this.problem.id });
    },
    restoreProblem() {
      this.$store.dispatch(`problem/${RESTORE_PROBLEM}`, { id: this.problem.id });
    },
    /** Close the problem sheet modal */
    closeModal() {
      this.$router.back();
    },
    beforeShowAI(next) {
      const data = IAAnalyser.analyseProblemSheet(this, this.plans);
      this.iaAnalysisText = data.msg;
      this.iaAnchoredBtns = data.btns;
      next();
    },
    createHelpSteps() {
      this.problem = IAHelp.demoProblems(this, true);
      if (this.$refs.causesSection.editingFiveWhys && this.$refs.causesSection.$refs.fiveWhys) {
        return this.$refs.causesSection.$refs.fiveWhys.createHelpSteps() || [];
      }
      if (this.$refs.causesSection.editingIshikawa && this.$refs.causesSection.$refs.ishikawa) {
        return this.$refs.causesSection.$refs.ishikawa.createHelpSteps() || [];
      }

      return [
        new IAHelpStep(null, this.$t('help.SHEET_INTRO')),
        new IAHelpStep('.problem-sheet > .basic-gray-section', this.$t('help.SHEET_PB')),
        new IAHelpStep('.causes-section', this.$t('help.SHEET_CAUSES'), () => {
          this.selectCause(this.problem.wCauses[0]);
        }),
        new IAHelpStep('.causes-section .cause-item', this.$t('help.SHEET_CAUSE_ITEM')),
        new IAHelpStep(
          '.causes-section .cause-item .quick-actions .icon:nth-child(3)',
          this.$t('help.SHEET_CAUSE_ITEM_DEL'),
        ),
        new IAHelpStep(
          '.causes-section .cause-item .quick-actions .icon:nth-child(2)',
          this.$t('help.SHEET_CAUSE_ITEM_SELECT'),
        ),
        new IAHelpStep('.causes-seeking', this.$t('help.SHEET_CAUSES_SEEK')),
        new IAHelpStep('.actions-section', this.$t('help.SHEET_ACTIONS')),
        new IAHelpStep('.action-item', this.$t('help.SHEET_ACTION_ITEM')),
        new IAHelpStep('.action-item .expand', this.$t('help.SHEET_ACTION_EXPAND'), () => {
          $('.action-item .expand').trigger('click');
        }),
        new IAHelpStep('.action-item .expanded-content', this.$t('help.SHEET_ACTION_EXPANDED')),
        new IAHelpStep('.actions-section .add-buttons', this.$t('help.SHEET_ADD_ACTION')),
      ];
    },
    helpHidden() {
      this.problem = cloneDeep(this.stateProblem);
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.problem-sheet {

  &--in-popup.edition-modal {
    position: static;
    width: 100%;
    height: 100%;
  }

  & &--only-problem {
    .f-h-below-filters {
      flex-flow: column-reverse;
    }

    .problem-sheet__details__summary, .problem-sheet__data__causes, .problem-sheet__data__actions {
      display: none;
    }

    .problem-sheet__data__w5h1 {
      margin-right: 0px;
    }
  }

  .f-h-below-filters {
    display: flex;
    flex-flow: column;
  }

  &__action-states {
    margin-top: 2rem;

    &__state {
      margin-top: .3rem;

      .generic-background {
        padding: .5rem;
      }

      .v-centerer {
        display: inline-flex;
      }

      &__count {
        padding: .2rem .4rem;
      }
    }

    .legend {
      margin-right: .4rem
    }
  }

  &__details {
    margin-top: $page-content-padding;
    display: flex;
    align-items: stretch;

    &__summary, &__pb-data {
      min-width: 0px;
      border-radius: $main-border-radius;
      padding: .9rem;
    }

    &__summary {
      flex: 0 0 auto;
      margin-right: $page-content-padding;
    }

    &__pb-data {
      flex: 1;
    }
  }

  &__data {
    margin-top: $page-content-padding;
    display: flex;
    align-items: stretch;

    &__w5h1, &__causes, &__actions {
      min-width: 0px;
      border-radius: $main-border-radius;
      padding: .9rem;
      flex: 1;
    }

    &__w5h1, &__causes {
      margin-right: $page-content-padding;
    }
  }

  h2.problem-sheet__section-title.basic-sub-title {
    font-weight: 600;
    margin-top: 0px;
    margin-bottom: $problem-sheet-section-title-margin;
  }

  &__section-title-wrapper {
    margin-bottom: $problem-sheet-section-title-margin;
    justify-content: space-between;

    h2.problem-sheet__section-title.basic-sub-title {
      margin-bottom: 0px;
    }
  }

  &__row {
    display: flex;
    align-items: stretch;
  }

  &__cell {
    flex: 1;
    min-width: 0px;

    &--user-padding {

      .user-thumbnail {
        margin-right: .5rem;
      }

      .problem-sheet__cell__wrapper {
        padding: .6rem .4rem;
      }
    }

    &__wrapper {
      padding: .7rem;
      border-radius: $high-border-radius;
      cursor: pointer;

      &:hover {
        background: $color-shizen-light-cyan;
      }
    }
  }

  &__history {
    padding: 1rem;
    box-sizing: border-box;
    background: $color-body-background;

    .history-item {
      background: white;
      border-radius: $main-border-radius;
      padding: .7rem;
      margin-top: .5rem;

      .bottom-sep {
        display: none;
      }
    }
  }
}

@media (max-width: $smartphone-landscape) {
  .problem-sheet {

    &__details {
      flex-flow: column;

      &__summary {
        margin-right: 0;
      }

      &__pb-data {
        margin-top: $page-content-padding;
        flex: 0 0 auto;
      }
    }

    &__data {
      flex-flow: column;

      &__w5h1, &__causes, &__actions {
        margin-right: 0;
        flex: 0 0 auto;
      }

      &__causes, &__actions {
        margin-top: $page-content-padding;
      }
    }
  }
}

@media (max-width: $smartphone-portrait) {
  .problem-sheet {
    &__details__pb-data {
      .problem-sheet {
        &__row {
          flex-wrap: wrap;
        }

        &__cell {
          flex: 0 0 50%;
        }
      }
    }
  }
}
</style>
